import React from 'react';
import { ArrowLeft } from 'lucide-react';

interface ChatSkeletonProps {
  companionName?: string;
  companionImage?: string;
  onBackToHome?: () => void;
}

export const ChatSkeleton: React.FC<ChatSkeletonProps> = ({
  companionName = 'Loading...',
  companionImage,
  onBackToHome,
}) => {
  return (
    <div className="flex-1 flex flex-col bg-kind-deep h-full">
      {/* Fixed Header Skeleton */}
      <div className="bg-kind-deep border-b border-white/10 flex-shrink-0">
        {/* Mobile Header Skeleton */}
        <div className="lg:hidden flex items-center px-4 py-3">
          <button
            onClick={onBackToHome}
            className="p-2 mr-3 hover:bg-kind-container rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-kind-text-primary" />
          </button>

          <div className="w-10 h-10 bg-kind-container rounded-full animate-pulse mr-3"></div>

          <div className="flex-1">
            <div className="h-5 bg-kind-container rounded-lg animate-pulse w-32"></div>
          </div>
        </div>

        {/* Desktop Header Skeleton */}
        <div className="hidden lg:block px-8 py-6">
          <div className="max-w-4xl mx-auto">
            <div className="flex flex-col items-center text-center">
              <div className="w-20 h-20 bg-kind-container rounded-full animate-pulse border-2 border-kind-purple/30 mb-4"></div>
              <div className="h-8 bg-kind-container rounded-lg animate-pulse mb-2 w-48"></div>
              <div className="h-5 bg-kind-container rounded-lg animate-pulse w-96 max-w-2xl"></div>
            </div>
          </div>
        </div>
      </div>

      {/* Scrollable Messages Area Skeleton */}
      <div className="flex-1 overflow-y-auto px-4 lg:px-8 py-4 lg:py-6">
        <div className="max-w-4xl mx-auto space-y-4 lg:space-y-6">
          {/* Mock conversation skeleton */}
          {Array.from({ length: 6 }).map((_, index) => {
            const isUserMessage = index % 3 === 0; // Every 3rd message is from user

            if (isUserMessage) {
              // User message skeleton - right aligned
              return (
                <div key={index} className="flex flex-col items-end space-y-2">
                  <div className="flex items-center space-x-2">
                    <div className="h-4 bg-kind-container rounded-lg animate-pulse w-20"></div>
                    <div className="w-6 h-6 lg:w-8 lg:h-8 bg-kind-purple/30 rounded-full animate-pulse"></div>
                  </div>
                  <div className="bg-kind-container rounded-2xl px-3 lg:px-4 py-2 lg:py-3 max-w-xs lg:max-w-md chat-message">
                    <div className="space-y-1">
                      <div className="h-4 bg-kind-text-secondary/30 rounded animate-pulse w-full"></div>
                      {index % 2 === 0 && (
                        <div className="h-4 bg-kind-text-secondary/30 rounded animate-pulse w-3/4"></div>
                      )}
                    </div>
                  </div>
                </div>
              );
            } else {
              // Companion message skeleton - left aligned
              return (
                <div key={index} className="flex items-start space-x-3 lg:space-x-4">
                  <div className="w-8 h-8 lg:w-10 lg:h-10 bg-kind-container rounded-full animate-pulse flex-shrink-0"></div>
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-2">
                      <div className="h-4 bg-kind-container rounded-lg animate-pulse w-24"></div>
                    </div>

                    <div className="bg-kind-graphite rounded-2xl p-3 lg:p-4 chat-message">
                      <div className="space-y-2">
                        <div className="h-4 bg-kind-text-secondary/30 rounded animate-pulse w-full"></div>
                        <div className="h-4 bg-kind-text-secondary/30 rounded animate-pulse w-5/6"></div>
                        {index % 3 === 1 && (
                          <div className="h-4 bg-kind-text-secondary/30 rounded animate-pulse w-4/5"></div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            }
          })}

          {/* Typing indicator skeleton */}
          <div className="flex items-start space-x-3 lg:space-x-4">
            <div className="w-8 h-8 lg:w-10 lg:h-10 bg-kind-container rounded-full animate-pulse flex-shrink-0"></div>
            <div className="flex-1">
              <div className="flex items-center space-x-2 mb-2">
                <div className="h-4 bg-kind-container rounded-lg animate-pulse w-24"></div>
              </div>

              <div className="bg-kind-graphite rounded-2xl p-3 lg:p-4">
                <div className="flex space-x-1">
                  <div className="w-2 h-2 bg-kind-text-secondary/50 rounded-full animate-bounce"></div>
                  <div
                    className="w-2 h-2 bg-kind-text-secondary/50 rounded-full animate-bounce"
                    style={{ animationDelay: '0.1s' }}
                  ></div>
                  <div
                    className="w-2 h-2 bg-kind-text-secondary/50 rounded-full animate-bounce"
                    style={{ animationDelay: '0.2s' }}
                  ></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Fixed Input Skeleton */}
      <div className="bg-kind-deep border-t border-white/10 flex-shrink-0 px-4 lg:px-8 py-4">
        <div className="max-w-4xl mx-auto">
          <div className="bg-kind-graphite rounded-2xl p-3 lg:p-4">
            <div className="flex items-end space-x-3 lg:space-x-4">
              <div className="flex-1 relative">
                <div className="h-6 bg-kind-text-secondary/30 rounded animate-pulse w-full"></div>
              </div>

              <div className="p-2 bg-kind-purple/30 text-white rounded-full animate-pulse flex-shrink-0 w-10 h-10"></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChatSkeleton;
